// Collection pills — Rhode-style instant collection switcher (with optional dropdown pills).
//
// Pills live in their own block (usually above the grid). On load we prefetch each collection's
// product grid; tapping a pill swaps the current collection grid (.collection-wrapper) in place —
// no full page reload — updates the active pill, and pushes the collection URL (history API) for
// shareability + back/forward. Progressive enhancement: the pills are real <a href> links, so with
// no JS (or if a swap fails) they navigate normally.
//
// A pill can also be a DROPDOWN (ARIA disclosure): a <button data-pill-toggle> reveals a panel of
// child collection links (e.g. Flip Flops → Men's / Women's). The toggle lights up when its own
// collection OR any child link matches the current collection; Esc / outside-click / choosing an
// item closes it. The child links are ordinary [data-collection-pill] links, so they swap + mark
// active through the same path as top-level pills.
if (!customElements.get('collection-pills-ext')) {
  class CollectionPillsExt extends HTMLElement {
    connectedCallback() {
      this.pills = [...this.querySelectorAll('[data-collection-pill]')];
      this.toggles = [...this.querySelectorAll('[data-pill-toggle]')];
      if (!this.pills.length && !this.toggles.length) return;

      // Grid we swap. Scope to the main results list so we never touch cart/reco grids.
      this.grid = document.querySelector('results-list .collection-wrapper') ||
                  document.querySelector('.product-grid-container .collection-wrapper');

      this.cache = new Map(); // handle -> grid innerHTML
      // Section id of the main results list — lets us prefetch just that section (Section Rendering
      // API) instead of the whole collection document.
      this.sectionId = this.grid && this.grid.closest('results-list')
        ? this.grid.closest('results-list').getAttribute('section-id')
        : null;
      const current = this.#currentHandle();
      if (this.grid && current) this.cache.set(current, this.grid.innerHTML);

      this.#markActive(current);
      this.pills.forEach((p) => p.addEventListener('click', this.#onClick));
      this.toggles.forEach((t) => t.addEventListener('click', this.#onToggleClick));
      this.addEventListener('keydown', this.#onKeydown);
      document.addEventListener('click', this.#onDocClick);
      window.addEventListener('popstate', this.#onPopState);
      this.#prefetch();
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.#onPopState);
      document.removeEventListener('click', this.#onDocClick);
    }

    #currentHandle() {
      const m = window.location.pathname.match(/\/collections\/([^/?#]+)/);
      return m ? m[1] : null;
    }

    #markActive(handle) {
      this.pills.forEach((p) => {
        const active = p.dataset.collectionHandle === handle;
        p.classList.toggle('is-active', active);
        // Expose the current collection to assistive tech (visual .is-active alone isn't announced).
        if (active) p.setAttribute('aria-current', 'page');
        else p.removeAttribute('aria-current');
      });
      // A dropdown toggle is active when its OWN collection is current, or any of its child links is.
      this.toggles.forEach((t) => {
        const dropdown = t.closest('[data-pill-dropdown]');
        const childActive = dropdown
          ? [...dropdown.querySelectorAll('[data-collection-pill]')].some((a) => a.dataset.collectionHandle === handle)
          : false;
        const active = t.dataset.collectionHandle === handle || childActive;
        t.classList.toggle('is-active', active);
        if (active) t.setAttribute('aria-current', 'page');
        else t.removeAttribute('aria-current');
      });
    }

    #announce(handle) {
      const status = this.querySelector('[data-pills-status]');
      if (!status) return;
      const pill = this.pills.find((p) => p.dataset.collectionHandle === handle);
      if (pill) status.textContent = 'Showing ' + (pill.textContent || '').trim();
    }

    /* ---- dropdown (ARIA disclosure) ---- */
    #menuFor(toggle) {
      const id = toggle.getAttribute('aria-controls');
      if (!id) return null;
      return this.querySelector('#' + (window.CSS && CSS.escape ? CSS.escape(id) : id));
    }

    #openDropdown(toggle) {
      this.#closeAllDropdowns(toggle);
      const menu = this.#menuFor(toggle);
      if (menu) menu.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      toggle.closest('[data-pill-dropdown]')?.classList.add('is-open');
    }

    #closeDropdown(toggle) {
      const menu = this.#menuFor(toggle);
      if (menu) menu.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      toggle.closest('[data-pill-dropdown]')?.classList.remove('is-open');
    }

    #closeAllDropdowns(except) {
      this.toggles.forEach((t) => { if (t !== except) this.#closeDropdown(t); });
    }

    #onToggleClick = (event) => {
      event.preventDefault();
      const toggle = event.currentTarget;
      if (toggle.getAttribute('aria-expanded') === 'true') this.#closeDropdown(toggle);
      else this.#openDropdown(toggle);
    };

    #onKeydown = (event) => {
      if (event.key === 'Escape') {
        const open = this.toggles.find((t) => t.getAttribute('aria-expanded') === 'true');
        if (open) { this.#closeDropdown(open); open.focus(); }
        return;
      }
      // ArrowDown from a toggle opens the panel and moves focus to the first link.
      if (event.key === 'ArrowDown') {
        const toggle = event.target.closest && event.target.closest('[data-pill-toggle]');
        if (toggle) {
          event.preventDefault();
          this.#openDropdown(toggle);
          this.#menuFor(toggle)?.querySelector('[data-collection-pill]')?.focus();
        }
      }
    };

    // Close open dropdowns on an outside click (clicks inside are handled by the toggle / item).
    #onDocClick = (event) => {
      if (this.contains(event.target)) return;
      this.#closeAllDropdowns();
    };

    // Fetch just the results section when we know its id (much smaller than the full document).
    #fetchUrl(url) {
      if (!this.sectionId) return url;
      return url + (url.includes('?') ? '&' : '?') + 'section_id=' + encodeURIComponent(this.sectionId);
    }

    async #getGridHTML(handle, url, signal) {
      if (this.cache.has(handle)) return this.cache.get(handle);
      const res = await fetch(this.#fetchUrl(url), { headers: { 'X-Requested-With': 'fetch' }, signal });
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const wrap = doc.querySelector('.collection-wrapper');
      const html = wrap ? wrap.innerHTML : null;
      if (html != null) this.cache.set(handle, html); // don't cache a failed extract
      return html;
    }

    async #swapTo(url, handle, push = true) {
      if (!this.grid || !url) {
        window.location.href = url;
        return;
      }
      // Abort any in-flight swap so rapid taps don't race (last tap wins).
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      this.classList.add('is-loading');
      let html = null;
      try {
        html = await this.#getGridHTML(handle, url, this.controller.signal);
      } catch (e) {
        if (e && e.name === 'AbortError') return; // superseded by a newer tap
        html = null;
      }
      this.classList.remove('is-loading');

      if (html == null) {
        window.location.href = url; // graceful fallback (different template / fetch failure)
        return;
      }

      this.grid.innerHTML = html;
      this.#markActive(handle);
      this.#announce(handle);
      if (push) {
        history.pushState({ collectionPills: true, handle }, '', url);
      }
      // Let apps (e.g. Okendo review stars) re-init against the freshly swapped cards.
      document.dispatchEvent(new CustomEvent('collection:pills:swapped', { detail: { handle, url } }));
    }

    #onClick = (event) => {
      const pill = event.currentTarget;
      const url = pill.dataset.collectionUrl;
      // Choosing a dropdown item closes its panel afterwards.
      const closeAfter = () => { if (pill.hasAttribute('data-collection-pill-item')) this.#closeAllDropdowns(); };
      if (!url || !this.grid) { closeAfter(); return; } // no grid → let the link navigate
      if (pill.classList.contains('is-active')) {
        event.preventDefault();
        closeAfter();
        return;
      }
      event.preventDefault();
      this.#swapTo(url, pill.dataset.collectionHandle, true);
      closeAfter();
    };

    #onPopState = () => {
      const handle = this.#currentHandle();
      const pill = this.pills.find((p) => p.dataset.collectionHandle === handle);
      if (this.cache.has(handle) && this.grid) {
        this.grid.innerHTML = this.cache.get(handle);
        this.#markActive(handle);
      } else if (pill) {
        this.#swapTo(pill.dataset.collectionUrl, handle, false);
      } else {
        window.location.reload();
      }
    };

    #prefetch() {
      const current = this.#currentHandle();
      const run = () =>
        this.pills.forEach((p) => {
          const handle = p.dataset.collectionHandle;
          const url = p.dataset.collectionUrl;
          if (url && handle && handle !== current && !this.cache.has(handle)) {
            this.#getGridHTML(handle, url).catch(() => {});
          }
        });
      if ('requestIdleCallback' in window) requestIdleCallback(run, { timeout: 2500 });
      else setTimeout(run, 800);
    }
  }

  customElements.define('collection-pills-ext', CollectionPillsExt);
}
